import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Settings as SettingsIcon,
  Plug,
  ToggleLeft,
  ToggleRight,
  Database,
  Zap,
  CreditCard,
  MessageCircle,
  Sparkles,
  Activity,
  Building2,
  Server,
  Hash,
  Clock,
  AlertCircle,
  CheckCircle2,
  CircleSlash,
  RefreshCw,
  Play,
} from 'lucide-react';
import { apiClient, type Integration, type FeatureFlag } from '../lib/api.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import { cn, formatDateTime, formatRelative } from '../lib/utils.js';

const categoryConfig: Record<
  Integration['category'],
  { label: string; icon: typeof Plug; color: string }
> = {
  core: { label: 'Core', icon: Database, color: 'text-emerald-400' },
  gateway: { label: 'Gateway', icon: CreditCard, color: 'text-purple-400' },
  channel: { label: 'Channel', icon: MessageCircle, color: 'text-blue-400' },
  llm: { label: 'LLM', icon: Sparkles, color: 'text-amber-400' },
  observability: { label: 'Observability', icon: Activity, color: 'text-rose-400' },
};

const statusConfig: Record<
  Integration['status'],
  { label: string; color: string; icon: typeof CheckCircle2 }
> = {
  online: {
    label: 'online',
    color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    icon: CheckCircle2,
  },
  configured_offline: {
    label: 'configurado',
    color: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    icon: AlertCircle,
  },
  not_configured: {
    label: 'não configurado',
    color: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
    icon: CircleSlash,
  },
};

function FlagRow({
  flag,
  onToggle,
  pending,
}: {
  flag: FeatureFlag;
  onToggle: (id: string, enabled: boolean) => void;
  pending: boolean;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 px-4 py-3 hover:bg-muted/30">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-0.5">
          <code className="text-xs font-mono font-medium text-foreground">
            {flag.name}
          </code>
          {flag.enabled && (
            <Badge
              variant="outline"
              className="text-[10px] bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
            >
              ATIVA
            </Badge>
          )}
        </div>
        {flag.description && (
          <p className="text-xs text-muted-foreground">{flag.description}</p>
        )}
        <div className="text-[10px] text-muted-foreground mt-1">
          Atualizada {formatDateTime(flag.updatedAt)}
          {flag.ownerName && ` · owner ${flag.ownerName}`}
        </div>
      </div>
      <button
        disabled={pending}
        onClick={() => onToggle(flag.id, !flag.enabled)}
        className={cn(
          'shrink-0 inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors disabled:opacity-50',
          flag.enabled
            ? 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/30'
            : 'bg-muted/40 text-muted-foreground hover:bg-muted border border-border',
        )}
      >
        {flag.enabled ? (
          <>
            <ToggleRight className="h-3.5 w-3.5" /> Habilitada
          </>
        ) : (
          <>
            <ToggleLeft className="h-3.5 w-3.5" /> Desabilitada
          </>
        )}
      </button>
    </div>
  );
}

function IntegrationRow({ i }: { i: Integration }): JSX.Element {
  const cat = categoryConfig[i.category];
  const stat = statusConfig[i.status];
  const CatIcon = cat.icon;
  const StatIcon = stat.icon;
  return (
    <div className="flex items-start justify-between gap-3 px-4 py-3 hover:bg-muted/30">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <CatIcon className={cn('h-4 w-4 mt-0.5 shrink-0', cat.color)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-medium">{i.name}</span>
            <Badge variant="outline" className="text-[10px]">
              {cat.label}
            </Badge>
            {i.required && (
              <Badge
                variant="outline"
                className="text-[10px] bg-rose-500/15 text-rose-300 border-rose-500/30"
              >
                obrigatória
              </Badge>
            )}
          </div>
          {i.details && (
            <p className="text-xs text-muted-foreground">{i.details}</p>
          )}
          {i.latencyMs !== undefined && (
            <div className="text-[10px] text-muted-foreground mt-1 tabular-nums">
              latência {i.latencyMs}ms
            </div>
          )}
        </div>
      </div>
      <span
        className={cn(
          'shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border',
          stat.color,
        )}
      >
        <StatIcon className="h-3 w-3" />
        {stat.label}
      </span>
    </div>
  );
}

type Tab = 'integrations' | 'flags' | 'scheduler' | 'tenant';

const JOB_LABEL: Record<string, { label: string; desc: string }> = {
  ticto_sync: {
    label: 'Sync Ticto',
    desc: 'Puxa últimas 48h de vendas do worker v2.0 e atualiza contatos/orders',
  },
  abandoned_sync: {
    label: 'Sync Abandoned Cart',
    desc: 'Puxa carrinhos abandonados (PIX expirado, cartão recusado…) dos últimos 7 dias e popula /recuperacao',
  },
  recovery_reconcile: {
    label: 'Recovery Reconcile',
    desc: 'Fecha automaticamente leads cuja venda já foi aprovada (evita Patricia abordar quem comprou)',
  },
  backfill_ledger: {
    label: 'Backfill Ledger',
    desc: 'Cria JE balanceada + event payment.approved pra orders sem JE',
  },
  meta_sync: {
    label: 'Sync Meta Ads',
    desc: 'Insights diários (ontem+hoje) via Graph API: spend, impressions, actions',
  },
};

function SchedulerTab(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['scheduler-status'],
    queryFn: () => apiClient.schedulerStatus(),
    refetchInterval: 15_000,
  });

  const runMut = useMutation({
    mutationFn: (job: 'ticto_sync' | 'backfill_ledger' | 'meta_sync' | 'abandoned_sync' | 'recovery_reconcile') =>
      apiClient.schedulerRun(job),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scheduler-status'] });
      qc.invalidateQueries({ queryKey: ['dashboard-v2'] });
      qc.invalidateQueries({ queryKey: ['finance-overview'] });
      qc.invalidateQueries({ queryKey: ['team-overview'] });
    },
  });

  if (isLoading || !data) {
    return <div className="p-8 text-muted-foreground">Carregando scheduler...</div>;
  }

  const jobs: Array<{ name: 'ticto_sync' | 'backfill_ledger' | 'meta_sync' | 'abandoned_sync' | 'recovery_reconcile'; intervalMin: number }> = [
    { name: 'ticto_sync', intervalMin: data.intervals.ticto_sync },
    { name: 'abandoned_sync', intervalMin: data.intervals.abandoned_sync ?? 5 },
    { name: 'recovery_reconcile', intervalMin: data.intervals.recovery_reconcile ?? 5 },
    { name: 'backfill_ledger', intervalMin: data.intervals.backfill_ledger },
    { name: 'meta_sync', intervalMin: data.intervals.meta_sync },
  ];

  const runByJob = new Map(data.lastRuns.map((r) => [r.jobName, r]));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-cyan-400" /> Scheduler
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Jobs periódicos in-process (setInterval). Substitui sync manual. Em produção (Fase 2) migrar pra Temporal workflows.
          Status: <strong className={data.enabled ? 'text-emerald-300' : 'text-rose-300'}>
            {data.enabled ? 'ATIVO' : 'DESLIGADO'}
          </strong>{' '}
          (via env <code>SCHEDULER_ENABLED</code>).
        </p>
      </CardHeader>
      <CardContent className="divide-y divide-border">
        {jobs.map((j) => {
          const last = runByJob.get(j.name);
          const labelData = JOB_LABEL[j.name];
          const isPending = runMut.isPending && runMut.variables === j.name;
          return (
            <div key={j.name} className="py-4 first:pt-0 last:pb-0">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-bold">{labelData.label}</h4>
                    <code className="text-[10px] font-mono bg-muted/40 px-1.5 py-0.5 rounded">{j.name}</code>
                    {j.intervalMin > 0 ? (
                      <Badge variant="outline" className="text-[10px] bg-cyan-500/15 text-cyan-300 border-cyan-500/30">
                        a cada {j.intervalMin}min
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] bg-slate-500/15 text-slate-400 border-slate-500/30">
                        desabilitado
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{labelData.desc}</p>
                  {last ? (
                    <div className="mt-2 text-[11px] text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                      <span>
                        última: <strong className={last.status === 'ok' ? 'text-emerald-300' : 'text-rose-300'}>
                          {last.status}
                        </strong>
                      </span>
                      <span>· {formatRelative(last.startedAt)}</span>
                      {last.durationMs && <span>· {last.durationMs}ms</span>}
                      <span>· trigger: {last.trigger}</span>
                    </div>
                  ) : (
                    <div className="mt-2 text-[11px] text-muted-foreground">
                      ainda não executou nessa sessão
                    </div>
                  )}
                  {last?.summary && Object.keys(last.summary).length > 0 && (
                    <div className="mt-1 text-[10px] text-muted-foreground/70 font-mono">
                      {Object.entries(last.summary).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(' · ')}
                    </div>
                  )}
                  {last?.error && (
                    <div className="mt-1 text-[10px] text-rose-300">
                      erro: {last.error}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => runMut.mutate(j.name)}
                  disabled={isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 text-xs font-medium hover:bg-cyan-500/20 disabled:opacity-50"
                >
                  <Play className="h-3 w-3" /> {isPending ? 'Rodando…' : 'Forçar'}
                </button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export function Configuracoes(): JSX.Element {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('integrations');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['settings-overview'],
    queryFn: () => apiClient.settingsOverview(),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiClient.toggleFeatureFlag(id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings-overview'] }),
  });

  if (isLoading) {
    return <div className="p-8 text-muted-foreground">Carregando configurações...</div>;
  }
  if (isError || !data) {
    return <div className="p-8 text-rose-400">Erro: {String(error)}</div>;
  }

  const { flags, integrations, envMeta, tenant, counts } = data;
  const tabs: Array<{ id: Tab; label: string; count: number }> = [
    { id: 'integrations', label: 'Integrações', count: integrations.length },
    { id: 'flags', label: 'Feature Flags', count: flags.length },
    { id: 'scheduler', label: 'Scheduler', count: 5 },
    { id: 'tenant', label: 'Tenant & Ambiente', count: 0 },
  ];

  const integrationsByCategory = integrations.reduce<Record<string, Integration[]>>(
    (acc, i) => {
      (acc[i.category] = acc[i.category] ?? []).push(i);
      return acc;
    },
    {},
  );

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <SettingsIcon className="h-6 w-6 text-primary" />
            Configurações
          </h1>
          <p className="text-sm text-muted-foreground">
            Tenant · Integrações externas · Feature flags · Variáveis de ambiente
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === t.id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
            {t.count > 0 && (
              <span className="ml-1.5 text-[10px] text-muted-foreground tabular-nums">
                ({t.count})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Integrações */}
      {tab === 'integrations' && (
        <div className="space-y-4">
          {(['core', 'gateway', 'channel', 'llm', 'observability'] as const).map(
            (cat) => {
              const items = integrationsByCategory[cat] ?? [];
              if (items.length === 0) return null;
              const c = categoryConfig[cat];
              const Icon = c.icon;
              return (
                <Card key={cat}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <Icon className={cn('h-4 w-4', c.color)} />
                      {c.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-border/40">
                      {items.map((i) => (
                        <IntegrationRow key={i.id} i={i} />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            },
          )}
          <div className="text-xs text-muted-foreground">
            <strong>Status:</strong> online = healthcheck OK · configurado = secret/url
            presente mas serviço não conectado em produção · não configurado = secret
            ausente no <code>.env</code>.
          </div>
        </div>
      )}

      {/* Feature Flags */}
      {tab === 'flags' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Feature Flags</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Toggle dinâmico — mudanças refletem instantaneamente em prod sem deploy.
              Regra R15 (CLAUDE.md): toda feature nova entra como flag <em>off</em>.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border/40">
              {flags.map((flag) => (
                <FlagRow
                  key={flag.id}
                  flag={flag}
                  pending={
                    toggleMutation.isPending &&
                    toggleMutation.variables?.id === flag.id
                  }
                  onToggle={(id, enabled) => toggleMutation.mutate({ id, enabled })}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduler */}
      {tab === 'scheduler' && <SchedulerTab />}

      {/* Tenant & Env */}
      {tab === 'tenant' && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <Building2 className="h-4 w-4" /> Tenant
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Nome</span>
                <span className="font-medium">{tenant.displayName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Slug</span>
                <code className="font-mono text-xs">{tenant.slug}</code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">CNPJ</span>
                <span className="text-xs">
                  {tenant.cnpj ?? (
                    <Badge
                      variant="outline"
                      className="text-[10px] bg-amber-500/15 text-amber-300 border-amber-500/30"
                    >
                      Shadow Ledger (adiado · ADR 0004)
                    </Badge>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Ativo</span>
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium',
                    tenant.active
                      ? 'bg-emerald-500/15 text-emerald-300'
                      : 'bg-rose-500/15 text-rose-300',
                  )}
                >
                  {tenant.active ? 'sim' : 'não'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Criado</span>
                <span className="text-xs tabular-nums">
                  {formatDateTime(tenant.createdAt)}
                </span>
              </div>
              {tenant.metadata && Object.keys(tenant.metadata).length > 0 && (
                <div className="pt-2 border-t border-border/40">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
                    Metadata
                  </div>
                  <pre className="text-[10px] font-mono bg-muted/30 rounded p-2 overflow-x-auto">
                    {JSON.stringify(tenant.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <Server className="h-4 w-4" /> Ambiente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">NODE_ENV</span>
                <Badge variant="outline" className="text-[10px]">
                  {envMeta.nodeEnv}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Log level</span>
                <code className="text-xs font-mono">{envMeta.logLevel}</code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Timezone
                </span>
                <code className="text-xs font-mono">{envMeta.timezone}</code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Core API</span>
                <code className="text-xs font-mono truncate max-w-[200px]">
                  {envMeta.coreApiUrl}
                </code>
              </div>
              <div className="pt-2 border-t border-border/40 space-y-2">
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                  Budget LLM (R14)
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Diário</span>
                  <span className="tabular-nums">
                    ${envMeta.llmDailyBudgetUsd}/dia
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Mensal</span>
                  <span className="tabular-nums">
                    ${envMeta.llmMonthlyBudgetUsd}/mês
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <Hash className="h-4 w-4" /> Volume de dados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                {[
                  { label: 'Usuários', v: counts.activeUsers },
                  { label: 'Contatos', v: counts.contacts },
                  { label: 'Pedidos', v: counts.ordersTotal },
                  { label: 'Journal entries', v: counts.journalEntries },
                  { label: 'Cost events', v: counts.costEvents },
                  { label: 'Eventos', v: counts.eventsTotal },
                ].map((s) => (
                  <div key={s.label}>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                      {s.label}
                    </div>
                    <div className="text-xl font-bold tabular-nums">{s.v}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
